import {
  ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { map, Observable } from 'rxjs';
import {
  HLM_DIALOG_SHELL, HlmBtnDirective, HlmIconComponent,
  HlmNativeSelectDirective, HlmSpinnerComponent, ToastService,
} from '../../../ui';
import { IdAutocompleteComponent } from '../../../shared/id-autocomplete/id-autocomplete.component';
import { employeeToHit } from '../../../shared/id-autocomplete/employee-to-hit.util';
import { IdentificationTanApiService } from '../../../core/services/identification-tan-api.service';
import {
  GeneratedTan, GenerateTanArgs, ValidityHours,
} from '../../../core/services/identification-tan.model';
import { EmployeeApiService } from '../../../core/services/employee-api.service';
import { AuthService } from '../../../core/auth/auth.service';
import { LoggingService } from '../../../core/services/logging.service';
import { SearchResult } from '../../search/search.model';

/** Org email/UPN domain — the agent login (`vorname.nachname`) maps to
 *  `<login>@egroup.io` (e.g. `torsten.lenz@egroup.io`). Used to pre-select the
 *  Teams chat recipient. Single tenant; lift to environment if it ever differs. */
const TEAMS_UPN_DOMAIN = 'egroup.io';

export interface GenerateTanDialogData {
  customerId: number;
  userAccountId: number;
  customerName: string;
  username: string;
}

type Phase = 'form' | 'submitting' | 'result' | 'error';

/**
 * Two-phase dialog for issuing a `Mitarbeiter-TAN` (see EB-3660).
 *
 *   form      — target employee + validity duration
 *   submitting — network in flight
 *   result     — 6-digit TAN displayed + copy-to-clipboard
 *   error      — 5xx; form values retained so the user can retry
 */
@Component({
  selector: 'cmt-generate-tan-dialog',
  standalone: true,
  imports: [
    FormsModule,
    ...HLM_DIALOG_SHELL,
    CdkConnectedOverlay, CdkOverlayOrigin,
    HlmBtnDirective, HlmIconComponent, HlmNativeSelectDirective, HlmSpinnerComponent,
    IdAutocompleteComponent,
  ],
  templateUrl: './generate-tan-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenerateTanDialogComponent {
  readonly dialogRef = inject(MatDialogRef<GenerateTanDialogComponent, void>);
  readonly data: GenerateTanDialogData = inject(MAT_DIALOG_DATA);
  private readonly api = inject(IdentificationTanApiService);
  private readonly employees = inject(EmployeeApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(ToastService);

  // ---------- Signals (template references) ----------

  private readonly auth = inject(AuthService);
  private readonly loggingService = inject(LoggingService);

  readonly phase = signal<Phase>('form');
  readonly targetEmployeeId = signal<number | null>(null);
  readonly validityHours = signal<ValidityHours>(2);
  readonly canSubmit = computed(() => this.targetEmployeeId() != null && this.phase() !== 'submitting');
  readonly generated = signal<GeneratedTan | null>(null);
  readonly errorMsg = signal<string | null>(null);
  readonly justCopied = signal(false);
  private copyTimer: ReturnType<typeof setTimeout> | undefined;
  /** Open state of the Share dropdown overlay (CDK-overlay so it escapes the dialog clip). */
  readonly shareOpen = signal(false);

  /** Cached employee name lookup (resolved at submit time from `EmployeeApiService`). */
  private readonly employeeNameById = signal<Map<number, string>>(new Map());

  // ---------- Computed template helpers ----------

  readonly customerName = computed(() => this.data.customerName || `Customer ${this.data.customerId}`);
  readonly username = computed(() => this.data.username || '');

  readonly validityOptions: readonly ValidityHours[] = [1, 2, 6, 12];

  /** Text label shown next to the generated TAN ("For:").
   *  Uses `||` (not `??`) so empty strings — which happen when the
   *  `/employees?search=` endpoint omits firstName/lastName — fall through to
   *  the `Employee #<id>` fallback rather than rendering blank. */
  readonly targetLabel = computed(() => {
    const id = this.targetEmployeeId();
    if (id == null) return '—';
    const stored = this.employeeNameById().get(id)?.trim();
    return stored || `Employee #${id}`;
  });

  /** "in 2h" / "in 30m" coarse relative representation of validity. */
  readonly expiresRelative = computed(() => {
    const g = this.generated();
    if (g?.validUntil) {
      const minsLeft = Math.max(0, Math.round((new Date(g.validUntil).getTime() - Date.now()) / 60000));
      if (minsLeft >= 60) return `in ${Math.round(minsLeft / 60)}h`;
      return `in ${minsLeft}m`;
    }
    return `in ${this.validityHours()}h`;
  });

  // ---------- Actions ----------

  /** Search adapter for IdAutocompleteComponent — same wiring as the Call Viewer. */
  readonly employeeSearchFn = (q: string): Observable<SearchResult[]> =>
    this.employees.searchEmployees(q).pipe(map((list) => list.map(employeeToHit)));

  /** Currently-resolved label for the selected hit — drives the autocomplete's
   *  re-rendered "<id> · <label>" display when the form re-renders. */
  readonly selectedLabel = signal<string | null>(null);

  /** Agent login of the selected employee (format `nachname.vorname`), used to
   *  derive the Teams recipient display name. Carried in the hit's `accent`
   *  slot as `(login)`. */
  readonly targetLogin = signal<string | null>(null);

  onEmployeeHit(hit: SearchResult | null): void {
    if (!hit) {
      this.targetEmployeeId.set(null);
      this.selectedLabel.set(null);
      this.targetLogin.set(null);
      return;
    }
    const id = Number(hit.entityId);
    this.targetEmployeeId.set(Number.isFinite(id) ? id : null);
    // accent is "(login)" — strip the parens to recover the bare login.
    this.targetLogin.set(hit.accent?.replace(/[()]/g, '').trim() || null);
    // Compose a robust label: name + accent if both present, otherwise whichever
    // exists. Search endpoint sometimes omits firstName/lastName — fall back so
    // the chip and "For:" line never read literal "undefined".
    const baseName = hit.detail?.trim() ?? '';
    const accent = hit.accent ?? '';
    const composed = [baseName, accent].filter(s => s.length > 0).join(' ');
    this.selectedLabel.set(composed || null);
    if (Number.isFinite(id)) {
      this.employeeNameById.update(m => {
        const next = new Map(m);
        next.set(id, composed);
        return next;
      });
    }
  }

  onValidityChange(value: string): void {
    const n = Number(value) as ValidityHours;
    if (n === 1 || n === 2 || n === 6 || n === 12) {
      this.validityHours.set(n);
    }
  }

  submit(): void {
    if (!this.canSubmit()) return;

    const targetEmployeeId = this.targetEmployeeId();
    if (targetEmployeeId == null) return;

    const issuer = this.auth.currentUserId;
    if (!issuer) {
      this.toast.open('Not authenticated', 'OK', { duration: 5000, tone: 'error' });
      return;
    }

    this.phase.set('submitting');
    this.errorMsg.set(null);

    const args: GenerateTanArgs = {
      targetEmployeeId,
      userAccountId: this.data.userAccountId,
      issuedByEmployee: issuer,
      validityHours: this.validityHours(),
    };

    this.api.generateTan(args)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tan) => {
          this.generated.set(tan);
          this.phase.set('result');
          // EB-3798: NEVER log the TAN value — metadata only.
          this.loggingService.info('tan.generated', {
            customerId: this.data.customerId,
            userAccountId: this.data.userAccountId,
            targetEmployeeId: args.targetEmployeeId,
            validityHours: args.validityHours,
          });
        },
        error: (err) => {
          const status = err?.status ?? 0;
          if (status === 403) {
            this.toast.open('Not allowed to generate TAN', 'OK', { duration: 5000, tone: 'error' });
            this.dialogRef.close();
            return;
          }
          this.errorMsg.set('Couldn\'t generate TAN — please try again.');
          this.phase.set('error');
          this.toast.open('Couldn\'t generate TAN', 'OK', { duration: 5000, tone: 'error' });
        },
      });
  }

  copy(): void {
    const gen = this.generated();
    if (!gen) return;
    navigator.clipboard?.writeText(gen.tan);
    this.justCopied.set(true);
    clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => this.justCopied.set(false), 1500);
  }

  // ---- Share ------------------------------------------------------------
  // Copy/Email/Teams trio matching the Call Details dialog pattern. Copy
  // sends the bare TAN to the clipboard (single-purpose, fast); Email and
  // Teams include context (customer, UA, validity) so the recipient knows
  // what the code is for.

  /** Rich share text used as Email subject + body / Teams message.
   *  Keep it free of parentheses/colons — Teams truncates the deeplink `message`
   *  at such characters (the concrete "(until HH:MM)" expiry broke it), so the
   *  validity is expressed as the plain duration. */
  private tanShareText(): string {
    const gen = this.generated();
    if (!gen) return '';
    return `TAN ${gen.tan} for ${this.customerName()}`
      + ` · UA ${this.username()}`
      + ` · valid for ${this.validityHours()}h`;
  }

  onShareEmail(): void {
    const gen = this.generated();
    if (!gen) return;
    const subject = `TAN for ${this.customerName()}`;
    const body = this.tanShareText();
    const href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  }

  /** Teams chat recipient (UPN) for the selected employee.
   *  The agent login IS the AD account name, so the UPN is `<login>@egroup.io`
   *  (org email scheme, e.g. `torsten.lenz@egroup.io`). This resolves in the
   *  chat deeplink's `users=`; display names do NOT (verified) — and the login
   *  surname can differ from the current display name (legacy logins), so the
   *  login is the only reliable identity. Empty when no login is known. */
  teamsRecipient(): string {
    const login = this.targetLogin();
    return login ? `${login}@${TEAMS_UPN_DOMAIN}` : '';
  }

  onShareTeams(): void {
    const gen = this.generated();
    if (!gen) return;
    const msg = this.tanShareText();
    const recipient = this.teamsRecipient();
    // Chat deeplink (not the /share endpoint) so we can pre-select the recipient:
    // `users` takes the UPN and Teams resolves it into the To field. The
    // `msteams:` scheme opens the app but drops the payload (see calls.component)
    // — use the HTTPS variant.
    const params = [];
    if (recipient) params.push(`users=${encodeURIComponent(recipient)}`);
    params.push(`message=${encodeURIComponent(msg)}`);
    const teamsUrl = `https://teams.microsoft.com/l/chat/0/0?${params.join('&')}`;
    this.loggingService.usage('side_entrance.click', { target: 'teams', section: 'user_account' });
    window.open(teamsUrl, '_blank', 'noopener');
  }

  backToForm(): void {
    this.phase.set('form');
    this.errorMsg.set(null);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  close(): void {
    this.dialogRef.close();
  }
}
